import {
    useMutation,
    useQuery,
    useInfiniteQuery,
    useQueryClient,
} from '@tanstack/react-query';

import {useClient} from 'context/auth-context';
import {ContactType} from 'types/common';
import {
    CONTACT_BANK_ACCOUNTS_CACHE,
    CONTACT_DETAILS_CACHE,
    CONTACT_TRANSACTIONS_CACHE,
    CONTACTS_LIST_CACHE,
    CONTACTS_PAYMENTS_CACHE,
    ALL_CONTACTS_LIST_CACHE,
    CONTACT_PREVIEW_INVITE_EMAIL,
} from 'constants/cache';
import {PAGINATION_LIMIT} from 'constants/common';
import {getQueryWithParams} from 'helpers/common';
import {IBankAccount, IConnectInviteBusinessPartner, IContact} from 'types/api';
import {IUseGetInfiniteTransactionListResponse} from './transactions';
import {getIdsFromTags} from './util';
import {PartialBy} from 'types/utils/utilitytypes';

const useContactRequest = (
    id?: string | number | null,
    groups?: ContactGroups[],
) => {
    const client = useClient();
    return useQuery<IContact>({
        queryKey: [CONTACT_DETAILS_CACHE, {id, groups}],
        queryFn: () =>
            client(`/web_api/contacts/${id}`, {
                params: {
                    groups,
                },
            }),
        enabled: !!id,
        retry: false,
    });
};

const useBankAccountsRequest = (id?: string | null) => {
    const client = useClient();
    return useQuery<IBankAccount[]>({
        queryKey: [CONTACT_BANK_ACCOUNTS_CACHE, {id}],
        queryFn: () =>
            client(`/web_api/contacts/${id}/bank_accounts`, {
                params: {
                    groups: ['PayoutRoutingView'],
                },
            }),
        enabled: !!id,
    });
};

const useBankAccountDelete = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) =>
            client(`${id}`, {
                data: {},
                method: 'DELETE',
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CONTACT_BANK_ACCOUNTS_CACHE],
            }),
    });
};

const useContactsRequest = (queryParams?: string) => {
    const client = useClient();
    return useQuery<IContact[]>({
        queryKey: [ALL_CONTACTS_LIST_CACHE, {queryParams}],
        queryFn: () =>
            client(
                `/web_api/contacts${
                    queryParams ? `?${queryParams}` : '?pagination=false'
                }`,
                {},
            ),
    });
};

export type ContactFilters = Partial<{
    name: string;
}>;

type ContactGroups = 'IncludePaymentGroups';

export interface IUseGetInfiniteContacts {
    itemsCount?: number;
    filters?: ContactFilters;
    groups?: ContactGroups[];
}

export interface IUseGetInfiniteContactsResponse {
    '@context': string;
    '@id': string;
    '@type': string;
    'hydra:member': IContact[];
    'hydra:totalItems': number;
}

const useGetInfiniteContacts = ({
    filters = {},
    itemsCount = PAGINATION_LIMIT,
    groups,
}: IUseGetInfiniteContacts) => {
    const client = useClient();

    return useInfiniteQuery<IUseGetInfiniteContactsResponse>({
        queryKey: [CONTACTS_LIST_CACHE, {filters, groups}],
        queryFn: ({pageParam}) => {
            return client(
                '/web_api/contacts',
                {
                    params: {
                        page: pageParam,
                        itemsPerPage: itemsCount,
                        ...filters,
                        groups,
                    },
                },
                true,
            );
        },
        initialPageParam: 1,
        getNextPageParam: (lastGroup, allGroups) => {
            if (
                lastGroup['hydra:member'].length === itemsCount &&
                allGroups.length * itemsCount < lastGroup['hydra:totalItems']
            ) {
                return allGroups.length + 1;
            }
            return undefined;
        },
    });
};

const useGetContactTransactionsInfinite = (
    id?: string,
    itemsCount: number = PAGINATION_LIMIT,
) => {
    const client = useClient();
    return useInfiniteQuery<IUseGetInfiniteTransactionListResponse>({
        queryKey: [CONTACT_TRANSACTIONS_CACHE, {id}],
        queryFn: ({pageParam}) => {
            return client(
                `/web_api/contacts/${id}/transactions`,
                {
                    params: {
                        page: pageParam,
                        itemsPerPage: itemsCount,
                        'order[id]': 'DESC',
                    },
                },
                true,
            );
        },
        enabled: !!id,
        initialPageParam: 1,
        getNextPageParam: (lastGroup, allGroups) => {
            if (
                lastGroup['hydra:member'].length === itemsCount &&
                allGroups.length * itemsCount < lastGroup['hydra:totalItems']
            ) {
                return allGroups.length + 1;
            }
            return undefined;
        },
    });
};

export type CreateContact = PartialBy<
    ContactType,
    | 'trackingEmail'
    | 'address'
    | 'zip'
    | 'city'
    | '@id'
    | 'businessPartner'
    | 'country'
>;

const useCreateContactRequest = () => {
    const client = useClient();

    return useMutation<IContact, unknown, CreateContact>({
        mutationFn: data =>
            client('/web_api/contacts', {
                data,
                method: 'POST',
            }),
    });
};

const useDeleteContact = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) =>
            client(`${id}`, {
                data: {},
                method: 'DELETE',
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CONTACTS_LIST_CACHE],
            }),
    });
};

export type UpdateContact = Partial<CreateContact> & {trackingEmail?: string};

const useUpdateContactRequest = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: UpdateContact) => {
            const {'@id': id, tags, ...rest} = data;
            return client(`${id}`, {
                data: {
                    ...rest,
                    tags: getIdsFromTags(tags || []),
                },
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/vnd.api+json',
                },
            });
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CONTACT_DETAILS_CACHE],
            }),
    });
};

export type ContactInviteRequest = {
    email: string;
    personalText?: string;
    contact: string;
};

const useContactInviteRequest = () => {
    const client = useClient();

    return useMutation<
        IConnectInviteBusinessPartner,
        unknown,
        ContactInviteRequest
    >({
        mutationFn: data => {
            return client(`/web_api/contact_invites`, {
                data,
                method: 'POST',
            });
        },
    });
};

export interface IContactInviteEmailMessage {
    preview: string;
}

const useGetContactInviteEmailMessage = (contactId?: string) => {
    const client = useClient();
    return useQuery<IContactInviteEmailMessage>({
        queryKey: [CONTACT_PREVIEW_INVITE_EMAIL],
        queryFn: () =>
            client(`/web_api/contacts/${contactId}/preview-invite-email`, {}),
        enabled: false,
    });
};

export const useContactSearchRequest = () => {
    const client = useClient();

    return useMutation({
        mutationFn: (query: string, page = 1) =>
            client(
                `/web_api/contacts?name=${query}&page=${page}&itemsPerPage=50`,
                {},
            ),
    });
};

export interface IContactsPayment {
    '@id': string;
    '@type': string;
    currency: string;
    country: string;
    additionalFields: [
        {
            '@context': string;
            '@id': string;
            '@type': string;
            name: string;
            value: string;
        },
    ];
    accountNumber: string;
    iban: string;
    swift: string;
    bankName: string;
    contact: IContact;
    businessPartner: string;
}

export type IContactsPaymentRequestResponse = IContactsPayment[];

const useGetMostUsedPayoutAccounts = (count = 5) => {
    const client = useClient();
    return useQuery<IContactsPaymentRequestResponse>({
        queryKey: [CONTACTS_PAYMENTS_CACHE, {count}],
        queryFn: () =>
            client(
                `/web_api/bank_accounts?order_transaction_count=1&itemsPerPage=${count}&page=1`,
                {},
            ),
    });
};

export interface IUseUploadContactsRequest {
    bpId: string;
    file: File;
}

const useUploadContacts = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({bpId, file}: IUseUploadContactsRequest) => {
            const formData = new FormData();
            formData.append('file', file);
            return client(`${bpId}/contacts/contact_upload`, {
                data: formData,
                method: 'POST',
                headers: {'Content-Type': 'multipart/form-data'},
                keepOriginal: true,
            });
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CONTACTS_LIST_CACHE],
            }),
    });
};

export {
    useContactRequest,
    useBankAccountsRequest,
    useBankAccountDelete,
    useContactsRequest,
    useCreateContactRequest,
    useDeleteContact,
    useUpdateContactRequest,
    useContactInviteRequest,
    useGetContactInviteEmailMessage,
    useGetContactTransactionsInfinite,
    useGetMostUsedPayoutAccounts,
    useGetInfiniteContacts,
    useUploadContacts,
};
